import { useState, useCallback } from 'react';
import {
  Flex,
  Text,
  Input,
  Checkbox,
  Button,
  Icon,
  Separator,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  FieldGroup,
  Field,
  FieldLabel,
  FieldDescription,
  FieldTitle,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { type IEmailFiltersProps, type IEmailFilters } from './types';

/**
 * Default empty filter state
 */
const DEFAULT_FILTERS: IEmailFilters = {
  from: '',
  cc: '',
  subjectHasWords: '',
  subjectDoesntHaveWords: '',
  bodyHasWords: '',
  bodyDoesntHaveWords: '',
  hasAttachments: false,
};

/**
 * EmailFilters Component
 *
 * A collapsible component that displays email filter configuration options.
 * Allows users to filter emails by sender, CC, subject, body content,
 * and attachments.
 *
 * @param props - Component props
 * @returns JSX element containing the email filters form
 */
export function EmailFilters({
  filters,
  onFiltersChange,
  onClearAll,
  readOnly = false,
}: IEmailFiltersProps) {
  const { t } = useTranslation('automation-inputs');
  const [isOpen, setIsOpen] = useState(false);

  /**
   * Updates a single filter value
   */
  const updateFilter = useCallback(
    <K extends keyof IEmailFilters>(key: K, value: IEmailFilters[K]) => {
      onFiltersChange({
        ...filters,
        [key]: value,
      });
    },
    [filters, onFiltersChange],
  );

  /**
   * Handles clearing all filters
   */
  const handleClearAll = useCallback(() => {
    onFiltersChange(DEFAULT_FILTERS);
    onClearAll?.();
  }, [onFiltersChange, onClearAll]);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="w-full bg-card rounded-lg p-5"
    >
      {/* Collapsible Header */}
      <CollapsibleTrigger className="w-full">
        <Flex
          direction="row"
          align="start"
          justify="between"
          className="cursor-pointer"
        >
          <Flex direction="column" gap={1} align="start">
            <Text className="font-medium text-md">
              {t('emailDialog.filters.title')}
            </Text>
            <Text className="text-sm text-muted-foreground text-left">
              {t('emailDialog.filters.description')}
            </Text>
          </Flex>
          <Icon
            type={isOpen ? 'ChevronUp' : 'ChevronDown'}
            size="sm"
            className="text-muted-foreground shrink-0 mt-0.5"
          />
        </Flex>
      </CollapsibleTrigger>

      {/* Collapsible Content - Filter Fields */}
      <CollapsibleContent className="pt-4">
        <FieldGroup className="gap-4">
          {/* From */}
          <Field className="gap-2">
            <FieldLabel>{t('emailDialog.filters.from')}</FieldLabel>
            <Input
              type="email"
              placeholder={t('emailDialog.filters.fromPlaceholder')}
              value={filters.from ?? ''}
              onChange={e => updateFilter('from', e.target.value)}
              className="bg-background"
              disabled={readOnly}
            />
          </Field>

          {/* CC */}
          <Field className="gap-2">
            <FieldLabel>{t('emailDialog.filters.cc')}</FieldLabel>
            <Input
              type="email"
              placeholder={t('emailDialog.filters.ccPlaceholder')}
              value={filters.cc ?? ''}
              onChange={e => updateFilter('cc', e.target.value)}
              className="bg-background"
              disabled={readOnly}
            />
          </Field>

          <Separator />

          {/* Subject Section */}
          <FieldGroup>
            <FieldTitle>{t('emailDialog.filters.subject')}</FieldTitle>
            <Field className="gap-2">
              <FieldLabel>{t('emailDialog.filters.hasWords')}</FieldLabel>
              <Input
                type="text"
                placeholder={t(
                  'emailDialog.filters.subjectHasWordsPlaceholder',
                )}
                value={filters.subjectHasWords ?? ''}
                onChange={e => updateFilter('subjectHasWords', e.target.value)}
                className="bg-background"
                disabled={readOnly}
              />
            </Field>
            <Field className="gap-2">
              <FieldLabel>
                {t('emailDialog.filters.doesntHaveWords')}
              </FieldLabel>
              <Input
                type="text"
                placeholder={t(
                  'emailDialog.filters.subjectDoesntHaveWordsPlaceholder',
                )}
                value={filters.subjectDoesntHaveWords ?? ''}
                onChange={e =>
                  updateFilter('subjectDoesntHaveWords', e.target.value)
                }
                className="bg-background"
                disabled={readOnly}
              />
            </Field>
          </FieldGroup>

          <Separator />

          {/* Body Section */}
          <FieldGroup>
            <FieldTitle>{t('emailDialog.filters.body')}</FieldTitle>
            <Field className="gap-2">
              <FieldLabel>{t('emailDialog.filters.hasWords')}</FieldLabel>
              <Input
                type="text"
                placeholder={t('emailDialog.filters.bodyHasWordsPlaceholder')}
                value={filters.bodyHasWords ?? ''}
                onChange={e => updateFilter('bodyHasWords', e.target.value)}
                className="bg-background"
                disabled={readOnly}
              />
            </Field>
            <Field className="gap-2">
              <FieldLabel>
                {t('emailDialog.filters.doesntHaveWords')}
              </FieldLabel>
              <Input
                type="text"
                placeholder={t(
                  'emailDialog.filters.bodyDoesntHaveWordsPlaceholder',
                )}
                value={filters.bodyDoesntHaveWords ?? ''}
                onChange={e =>
                  updateFilter('bodyDoesntHaveWords', e.target.value)
                }
                className="bg-background"
                disabled={readOnly}
              />
            </Field>
          </FieldGroup>

          <Separator />

          {/* Has Attachments Section */}
          <FieldGroup>
            <Field className="gap-2">
              <Flex direction="row" gap={2}>
                <Checkbox
                  className="mt-0.5"
                  checked={filters.hasAttachments ?? false}
                  onCheckedChange={checked =>
                    updateFilter('hasAttachments', checked === true)
                  }
                  disabled={readOnly}
                />
                <Flex direction="column" gap={0.5}>
                  <FieldLabel>
                    {t('emailDialog.filters.hasAttachments')}
                  </FieldLabel>
                  <FieldDescription>
                    {t('emailDialog.filters.hasAttachmentsDescription')}
                  </FieldDescription>
                </Flex>
              </Flex>
            </Field>
          </FieldGroup>

          {/* Action Buttons - Only show in edit/create mode */}
          {!readOnly && (
            <Flex
              direction="row"
              justify="between"
              width="full"
              className="pt-2"
            >
              <Button variant="outline" onClick={handleClearAll}>
                {t('emailDialog.filters.clearAll')}
              </Button>
            </Flex>
          )}
        </FieldGroup>
      </CollapsibleContent>
    </Collapsible>
  );
}
